import {
    useState, useMemo, useRef, useCallback,
} from 'react';
import { SQUARE_FEET_PER_ACRE, UNIT_OF_MEASURE_TYPES, UNIT_TYPE_NAMES } from 'constants/unitTypes';
import { IGrossBuildableAreaLot } from 'views/ProFormaTable/types';
import { AreaMode } from 'views/ProFormaSetup/constants';

export const calculateBuildableArea = (lots: IGrossBuildableAreaLot[]) => {
    let totalGrossBuildableArea = 0;
    let totalFar = 0;
    let totalLandAreaSquareFootage = 0;

    lots.forEach(lot => {
        // Calculate GBA in square feet
        const grossBuildableArea = lot.areaMode === AreaMode.FAR
            ? lot.landArea
                * lot.floorAreaRatio
                * (lot.landAreaUnitTypeId === UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.ACRE].id
                    ? SQUARE_FEET_PER_ACRE : 1)
            : lot.grossBuildableArea || 0;

        totalGrossBuildableArea += grossBuildableArea;
        totalFar += lot.floorAreaRatio || 0;

        // Convert land area to square feet
        const landAreaSqFt = lot.landAreaUnitTypeId === UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.GROSS_SF].id
            ? lot.landArea
            : lot.landArea * SQUARE_FEET_PER_ACRE;
        totalLandAreaSquareFootage += landAreaSqFt;
    });

    return {
        totalGrossBuildableArea,
        totalFar,
        totalLandAreaSquareFootage,
    };
};

export const useGrossBuildableArea = (initialLots: IGrossBuildableAreaLot[] = []) => {
    const [lots, setLots] = useState<IGrossBuildableAreaLot[]>(initialLots.length > 0 ? initialLots : []);
    const lastIdRef = useRef(initialLots.length > 0 ? Math.max(...initialLots.map(lot => lot.id)) : 1);

    // Create a new lot
    const createLot = useCallback((landArea?: number, floorAreaRatio?: number, id?: number) => {
        const newLot: IGrossBuildableAreaLot = {
            id: id || ++lastIdRef.current,
            landArea: landArea || 0,
            landAreaUnitTypeId: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.GROSS_SF].id,
            floorAreaRatio: floorAreaRatio || 0,
            grossBuildableArea: landArea && floorAreaRatio ? landArea * floorAreaRatio : 0,
            areaMode: floorAreaRatio ? AreaMode.FAR : AreaMode.GROSS_BUILD_AREA,
        };
        setLots(prevLots => [...prevLots, newLot]);
    }, []);

    // Update an existing lot
    const updateLot = (id: number, field: keyof IGrossBuildableAreaLot, value: string | number) => {
        setLots(prevLots => prevLots.map(lot => {
            if (lot.id === id) {
                const updatedLot = { ...lot, [field]: value };

                // Area mode to FAR should reset grossBuildableArea
                if (field === 'areaMode' && value === AreaMode.FAR) updatedLot.grossBuildableArea = 0;

                // If in FAR mode and land area, FAR, or unit type change, recalculate in sqft
                if (updatedLot.areaMode === AreaMode.FAR && ['landArea', 'floorAreaRatio', 'landAreaUnitTypeId'].includes(field as string)) {
                    const landAreaSqFt = updatedLot.landAreaUnitTypeId === UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.ACRE].id
                        ? updatedLot.landArea * SQUARE_FEET_PER_ACRE
                        : updatedLot.landArea;
                    updatedLot.grossBuildableArea = landAreaSqFt * updatedLot.floorAreaRatio;
                }

                return updatedLot;
            }
            return lot;
        }));
    };

    // Delete a lot
    const deleteLot = (id: number) => {
        setLots(lots.filter(lot => lot.id !== id));
    };

    // Clear all lots
    const clearLots = useCallback(() => {
        setLots([]);
        lastIdRef.current = 1;
    }, []);

    const updateAllLots = useCallback((newLots: IGrossBuildableAreaLot[]) => {
        setLots(newLots);
        if (newLots.length > 0) {
            lastIdRef.current = Math.max(...newLots.map(lot => lot.id));
        }
    }, []);

    // Calculate total gross buildable area - GBA is always in square feet
    const totalGrossBuildableArea = useMemo(() => lots.reduce((total, lot) => {
        // Get gross buildable area based on mode (backend _get_gross_buildable_area logic)
        const grossBuildableArea = lot.areaMode === AreaMode.FAR
            ? lot.landArea
                * lot.floorAreaRatio
                * (lot.landAreaUnitTypeId === UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.ACRE].id
                    ? SQUARE_FEET_PER_ACRE : 1)
            : lot.grossBuildableArea || 0;

        return total + grossBuildableArea;
    }, 0), [lots]);

    // Additional totals needed for quickstart
    const totalFar = useMemo(() => lots.reduce((total, lot) => total + (lot.floorAreaRatio || 0), 0), [lots]);

    const totalLandAreaSquareFootage = useMemo(() => lots.reduce((total, lot) => {
        const landAreaSqFt = lot.landAreaUnitTypeId === UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.GROSS_SF].id
            ? lot.landArea
            : lot.landArea * SQUARE_FEET_PER_ACRE;
        return total + landAreaSqFt;
    }, 0), [lots]);

    return {
        lots,
        createLot,
        updateLot,
        deleteLot,
        clearLots,
        updateAllLots,
        totalGrossBuildableArea,
        totalFar,
        totalLandAreaSquareFootage,
    };
};
